import { Canvas } from '@react-three/fiber';
import { Center, Environment, Grid, OrbitControls, useGLTF } from '@react-three/drei';
import {
  Component,
  Suspense,
  useEffect,
  useMemo,
  type ErrorInfo,
  type ReactNode,
} from 'react';
import * as THREE from 'three';
import { probeViewerCapabilities } from '../lib/viewerCapabilities';

export type InspectMode = 'shaded' | 'clay' | 'wireframe';
export type LightingPreset = 'studio' | 'daylight' | 'gallery' | 'evening' | 'industrial';

export type PresentationSettings = {
  lightingPreset?: LightingPreset;
  environmentIntensity?: number;
  exposure?: number;
  roughness?: number;
  metalness?: number;
  tint?: string;
  inspectMode?: InspectMode;
  showGrid?: boolean;
};

const ENV_MAP: Record<LightingPreset, 'studio' | 'city' | 'apartment' | 'sunset' | 'warehouse'> = {
  studio: 'studio',
  daylight: 'city',
  gallery: 'apartment',
  evening: 'sunset',
  industrial: 'warehouse',
};

function Model({
  url,
  settings,
}: {
  url: string;
  settings: PresentationSettings;
}) {
  const gltf = useGLTF(url);
  useEffect(() => {
    return () => {
      useGLTF.clear(url);
    };
  }, [url]);

  useEffect(() => {
    const mode = settings.inspectMode ?? 'shaded';
    gltf.scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const mat of mats) {
        const m = mat as THREE.MeshStandardMaterial;
        if (!m) continue;
        if (mode === 'wireframe') {
          m.wireframe = true;
        } else {
          m.wireframe = false;
        }
        if (mode === 'clay') {
          if ('color' in m && m.color) m.color.set('#c8c8c8');
          if ('roughness' in m) m.roughness = 1;
          if ('metalness' in m) m.metalness = 0;
          if ('map' in m) m.map = null;
        } else {
          if (settings.tint && 'color' in m && m.color) m.color.set(settings.tint);
          if (typeof settings.roughness === 'number' && 'roughness' in m) {
            m.roughness = settings.roughness;
          }
          if (typeof settings.metalness === 'number' && 'metalness' in m) {
            m.metalness = settings.metalness;
          }
        }
        m.needsUpdate = true;
      }
    });
  }, [gltf.scene, settings]);

  return (
    <Center>
      <primitive object={gltf.scene} />
    </Center>
  );
}

class ViewerErrorBoundary extends Component<
  { children: ReactNode; onError: (message: string) => void },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    this.props.onError(error.message || 'Failed to render 3D object');
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

export function ViewerCanvas({
  runtimeUrl,
  posterUrl,
  title,
  autoRotate = false,
  status = 'idle',
  error = null,
  presentation,
  darkCanvas = false,
}: {
  runtimeUrl: string | null;
  posterUrl?: string | null;
  title?: string;
  autoRotate?: boolean;
  status?: 'idle' | 'loading' | 'ready' | 'error';
  error?: string | null;
  presentation?: PresentationSettings;
  darkCanvas?: boolean;
}) {
  const caps = useMemo(() => probeViewerCapabilities(), []);
  const prefersReducedMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const effectiveAutoRotate = autoRotate && !prefersReducedMotion;
  const settings: PresentationSettings = presentation ?? {};
  const preset = settings.lightingPreset ?? 'studio';
  const bg = darkCanvas ? '#191919' : '#f7f7f7';

  if (!caps.webgl2) {
    return (
      <div className="viewer-canvas viewer-canvas--message viewer-canvas--poster" role="region" aria-label="Poster preview">
        {posterUrl ? (
          <img src={posterUrl} alt={title ? `Poster for ${title}` : 'Object poster'} />
        ) : (
          <div className="panel">
            <p style={{ margin: 0 }}>Live 3D preview is unavailable on this device.</p>
            <p className="muted" style={{ margin: '0.5rem 0 0' }}>
              You can still inspect the images and asset details.
            </p>
          </div>
        )}
      </div>
    );
  }

  if (status === 'loading' || (!runtimeUrl && status !== 'error' && status !== 'idle')) {
    return (
      <div className="viewer-canvas viewer-canvas--message" role="status">
        <div className="panel">Loading 3D runtime…</div>
      </div>
    );
  }

  if (status === 'idle' && !runtimeUrl) {
    return (
      <div className="viewer-canvas viewer-canvas--message" role="status" style={{ background: bg }}>
        <div className="panel" style={{ textAlign: 'center', maxWidth: 360 }}>
          <p style={{ margin: 0, fontWeight: 600 }}>Upload an image, describe an object, or import a model</p>
          <p className="muted" style={{ margin: '0.5rem 0 0', fontSize: '0.875rem' }}>
            This empty stage is ready for creation — it is not a failed renderer.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'error' || error) {
    return (
      <div className="viewer-canvas viewer-canvas--message viewer-canvas--poster" role="alert">
        {posterUrl ? <img src={posterUrl} alt={title ? `Poster for ${title}` : 'Object poster'} /> : null}
        <div className="panel" style={{ marginTop: posterUrl ? '1rem' : 0 }}>
          <p className="err" style={{ margin: 0 }}>
            {error ?? 'Could not load this object'}
          </p>
          <p className="muted" style={{ margin: '0.5rem 0 0', fontSize: '0.875rem' }}>
            Poster and asset details remain available.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="viewer-canvas" role="img" aria-label="Interactive 3D viewer">
      <Canvas
        camera={{ position: [1.6, 1.2, 1.8], fov: 45 }}
        dpr={[1, 1.75]}
        gl={{ antialias: true, powerPreference: 'high-performance', toneMappingExposure: 1 + (settings.exposure ?? 0) * 0.35 }}
      >
        <color attach="background" args={[bg]} />
        <ambientLight intensity={0.55 * (settings.environmentIntensity ?? 1)} />
        <directionalLight position={[4, 6, 2]} intensity={1.1 * (settings.environmentIntensity ?? 1)} />
        <ViewerErrorBoundary onError={(message) => console.error(message)}>
          <Suspense fallback={null}>
            {runtimeUrl ? <Model url={runtimeUrl} settings={settings} /> : null}
            {caps.environmentOk ? <Environment preset={ENV_MAP[preset]} /> : null}
            {settings.showGrid ? (
              <Grid infiniteGrid fadeDistance={12} sectionColor="#444" cellColor="#333" />
            ) : null}
          </Suspense>
        </ViewerErrorBoundary>
        <OrbitControls makeDefault autoRotate={effectiveAutoRotate} autoRotateSpeed={0.6} />
      </Canvas>
    </div>
  );
}
